import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Save, Lock, Pencil, X } from "lucide-react";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ all_modules: [], groups: {} });
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [a, b] = await Promise.all([
        api.get("/roles"),
        api.get("/roles/modules"),
      ]);
      setRoles(a.data); setCatalog(b.data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({ id: null, name: "", modules: ["dashboard"], description: "" });

  // Friendly labels for dep-hint messages
  const MODULE_LABEL = {
    dashboard: "Dashboard", roasts: "Roasts", imports: "Imports",
    origins: "Origins", suppliers: "Suppliers", machines: "Machines",
    operators: "Operators", blends: "Blends", inventory: "Inventory",
    monthly_costs: "Monthly costs", cupping: "Cupping", users: "Users",
    settings: "Settings", bills: "Bills", journals: "Journals",
    sales_targets: "Sales targets", equipment: "Equipment",
    manufacturing_cost: "Manufacturing cost", item_mapping: "Item mapping",
  };

  const save = async () => {
    setError("");
    try {
      const payload = {
        name: editing.name.trim(),
        modules: editing.modules,
        description: editing.description || "",
      };
      if (editing.id) await api.put(`/roles/${editing.id}`, payload);
      else            await api.post("/roles", payload);
      setEditing(null);
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="roles-page">
      <PageHeader
        eyebrow="Admin"
        title="Roles"
        description="Define which app modules each role can access. The Owner role has access to everything and is locked. Other roles can be renamed, edited, or deleted (only if no users are assigned)."
        actions={
          <button onClick={() => setEditing(blank())} data-testid="role-new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
            <Plus className="w-4 h-4" /> New role
          </button>
        }
      />

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4">{error}</div>}

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left p-3 label-tiny">Role</th>
              <th className="text-left p-3 label-tiny">Description</th>
              <th className="text-right p-3 label-tiny">Modules</th>
              <th className="text-right p-3 label-tiny w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="4" className="p-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && roles.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`role-row-${r.id}`}>
                <td className="p-3 font-semibold flex items-center gap-2">
                  {r.is_locked && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                  {r.name}
                </td>
                <td className="p-3 text-muted-foreground text-xs">{r.description || "—"}</td>
                <td className="p-3 text-right font-mono text-xs">
                  {r.modules.length} / {catalog.all_modules.length}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditing({ ...r })} data-testid={`role-edit-${r.id}`}
                      className="p-1.5 border border-border rounded hover:bg-secondary text-muted-foreground" title="Edit">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!r.is_locked && (
                      <button onClick={() => remove(r)} data-testid={`role-delete-${r.id}`}
                        className="p-1.5 border border-destructive/30 rounded hover:bg-destructive/10 text-destructive" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <RoleEditModal
          role={editing} setRole={setEditing} catalog={catalog} labels={MODULE_LABEL}
          onSave={save} onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// Expand a list of modules with all transitive dependencies from the catalog.
function expandWithDeps(mods, deps) {
  const out = new Set(mods);
  const stack = [...mods];
  while (stack.length) {
    const m = stack.pop();
    for (const d of (deps?.[m] || [])) {
      if (!out.has(d)) { out.add(d); stack.push(d); }
    }
  }
  return [...out];
}

function RoleEditModal({ role, setRole, catalog, labels, onSave, onClose }) {
  const deps = catalog.dependencies || {};
  // Reverse map: which modules require this one (used to warn before unticking — but only if the dependent is ALSO ticked)
  const reverseDeps = (() => {
    const r = {};
    for (const [m, ds] of Object.entries(deps)) {
      for (const d of ds) (r[d] = r[d] || []).push(m);
    }
    return r;
  })();

  const toggle = (mod) => {
    if (role.is_locked) return;
    const isOn = role.modules.includes(mod);
    setRole({
      ...role,
      modules: isOn
        ? role.modules.filter((m) => m !== mod)
        : [...role.modules, mod],
    });
  };

  const toggleGroup = (mods) => {
    if (role.is_locked) return;
    const allOn = mods.every((m) => role.modules.includes(m));
    setRole({
      ...role,
      modules: allOn
        ? role.modules.filter((m) => !mods.includes(m))
        : [...new Set([...role.modules, ...mods])],
    });
  };

  // What silent data-access will this role get from dependencies, beyond
  // what they explicitly ticked? (e.g. ticking sales_targets gives read access
  // to blends + item_mapping data — without showing them in the sidebar.)
  const silentReads = (() => {
    const expanded = new Set(expandWithDeps(role.modules, deps));
    return [...expanded].filter((m) => !role.modules.includes(m));
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="role-edit-modal">
      <div className="bg-card border border-border rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card">
          <h3 className="font-display text-lg font-bold">{role.id ? "Edit" : "New"} role</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="label-tiny mb-1">Role name *</p>
              <input type="text" value={role.name} onChange={(e) => setRole({ ...role, name: e.target.value })}
                disabled={role.is_locked} data-testid="role-name"
                className="w-full px-3 py-2 border border-border rounded text-sm disabled:bg-muted" />
              {role.is_locked && <p className="text-[10px] text-muted-foreground mt-1">Owner role is locked.</p>}
            </div>
            <div>
              <p className="label-tiny mb-1">Description</p>
              <input type="text" value={role.description || ""} onChange={(e) => setRole({ ...role, description: e.target.value })}
                disabled={role.is_locked}
                className="w-full px-3 py-2 border border-border rounded text-sm disabled:bg-muted" />
            </div>
          </div>

          <div>
            <p className="label-tiny mb-2">Module access ({role.modules.length} / {catalog.all_modules.length})</p>
            <p className="text-xs text-muted-foreground mb-3">
              Tick only the modules you want this role to <strong>see in their menu</strong>. Some modules
              automatically read related data behind the scenes — listed below — without showing those
              modules in the sidebar.
            </p>

            {silentReads.length > 0 && (
              <div className="mb-3 text-xs bg-primary/5 border border-primary/30 rounded p-2.5 flex items-start gap-2"
                   data-testid="role-silent-reads">
                <span className="text-primary font-bold">🔒</span>
                <span>
                  <span className="font-semibold">Silent data access</span> (NOT shown in sidebar):{" "}
                  <span className="font-mono font-semibold">
                    {silentReads.map((m) => labels[m] || m).join(", ")}
                  </span>
                  . These let the ticked screens load their data — users can&apos;t navigate to them or edit them.
                </span>
              </div>
            )}

            <div className="space-y-3">
              {Object.entries(catalog.groups).map(([groupName, mods]) => {
                const checked = mods.filter((m) => role.modules.includes(m));
                const allOn = mods.length > 0 && checked.length === mods.length;
                const someOn = checked.length > 0 && !allOn;
                return (
                  <div key={groupName} className="border border-border rounded p-3">
                    <label className="flex items-center justify-between mb-2">
                      <span className="font-display font-bold text-sm">{groupName}</span>
                      <button type="button" onClick={() => toggleGroup(mods)}
                        disabled={role.is_locked} data-testid={`role-group-toggle-${groupName.replace(/\s+/g,'-').toLowerCase()}`}
                        className="text-xs text-primary hover:underline disabled:opacity-50">
                        {allOn ? "Deselect all" : someOn ? "Select all" : "Select all"}
                      </button>
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {mods.map((m) => {
                        const myDeps = deps[m] || [];
                        const dependents = reverseDeps[m] || [];
                        const dependentsTicked = dependents.filter((d) => role.modules.includes(d));
                        const isImplicit = !role.modules.includes(m) && silentReads.includes(m);
                        const tooltip = myDeps.length
                          ? `Auto-reads: ${myDeps.map((d) => labels[d] || d).join(", ")}`
                          : dependentsTicked.length
                            ? `Read silently because ${dependentsTicked.map((d) => labels[d] || d).join(", ")} need${dependentsTicked.length === 1 ? "s" : ""} it`
                            : null;
                        return (
                          <label key={m} title={tooltip || undefined}
                                 className={`flex items-center gap-2 text-xs ${role.is_locked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                            <input type="checkbox" checked={role.modules.includes(m)}
                              onChange={() => toggle(m)}
                              disabled={role.is_locked}
                              data-testid={`role-mod-${m}`}
                              className="accent-[hsl(20,39%,39%)]" />
                            <span className="font-mono">{m}</span>
                            {isImplicit && (
                              <span className="text-[10px] text-primary" title="Read silently — not in sidebar">
                                (silent)
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
            <button onClick={onSave} disabled={!role.name.trim()} data-testid="role-save"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
              <Save className="w-4 h-4" /> Save role
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
